import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Input from "../components/form/Input";
import Select from "../components/form/Select";
import IconSelect from "../components/form/IconSelect";
import { Link, useParams } from "react-router-dom";
import PredefinedJob from "../api/PredefinedJob";
import Category from "../api/Category";
import { toast } from "react-hot-toast";
import { DashboardLayout } from "../components/organisms/Layout";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../pages/JobPosition.scss";
import { useSelector } from "react-redux";
import { darkmode } from "../store/authSlice";

const PredefinedJobTemplate = () => {
  const { t } = useTranslation();
  const params = useParams();
  const mode = useSelector(darkmode);

  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [selectedCategory, setSelectedCategory] = useState();
  const [categories, setCategories] = useState([]);
  const [availability, setAvailability] = useState("");
  const [description, setDescription] = useState("");
  const [experience, setExperience] = useState("");
  const [selectedIcon, setSelectedIcon] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [edit, setEdit] = useState(false);

  useEffect(() => {
    document.title = "HeyHire Portal-Edit Position Template";
    loadCategories();
    loadJob();
  }, []);

  const loadJob = () => {
    setLoading(true);

    PredefinedJob.getById(params.jobId)
      .then((response) => {
        response = response?.data?.data;
        setTitle(response?.title);
        setStartDate(response?.start_date);
        setDescription(response?.description);
        setExperience(response?.experience);
        setAvailability(response?.availability);
        setSelectedCategory(response?.category_id);
        setSelectedIcon(response?.icon_name || "");
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error(t("common.something-went-wrong"));
        }
      })
      .then(() => setLoading(false));
  };

  const loadCategories = () => {
    Category.getAll().then((response) => {
      setCategories(response.data.data);
    });
  };

  const clearFields = () => {
    setTitle("");
    setStartDate(null);
    setDescription("");
    setExperience("");
    setAvailability("");
    setSelectedCategory(-1);
    setSelectedIcon("");
  };

  const updateJob = () => {
    setLoading(true);
    setValidationErrors([]);

    PredefinedJob.update(params.jobId, {
      title: title,
      start_date: startDate,
      availability: availability,
      description: description,
      experience: experience,
      category_id: selectedCategory,
      icon_name: selectedIcon,
    })
      .then(() => toast.success(t("pre-defined-jobs.pre-defined-job-updated")))
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          setValidationErrors(error.response.data.errors);
        } else {
          if (error?.response?.status !== 403) {
            toast.error(t("common.something-went-wrong-please-try-again"));
          }
        }
      })
      .then(() => setLoading(false));
  };

  return (
    <DashboardLayout >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between mb-8">
          <div className="min-w-0 flex-1">
            <div className="flex items-center">
              <Link
                to="/predefined-jobs"
                className="mr-4 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <i className="far fa-chevron-left text-xl" />
              </Link>
              <div>
                <h2 className="text-2xl font-bold leading-7 text-gray-900">
                  {t("pre-defined-jobs.pre-defined-job-template")}
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  edit job template description
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4 flex md:ml-4 md:mt-0">
            <button
              type="button"
              onClick={() => setEdit((e) => !e)}
              className="inline-flex items-center gap-x-2 rounded-lg px-4 py-2.5 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-all"
            >
              <i
                className={`fad fa-lock${edit ? "-open" : ""} text-gray-400`}
              />
              {t("common.edit")}
            </button>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              updateJob();
            }}
            className="space-y-8"
          >
            {/* Basic Information Section */}
            <div>
              <h3 className="text-base font-semibold leading-7 text-gray-900">
                {t("pre-defined-jobs.basic-information")}
              </h3>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                Basic details about the job position.
              </p>

              <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                <div className="col-span-full sm:col-span-1">
                  <Input
                    name="title"
                    type="text"
                    label={t("pre-defined-jobs.job-title")}
                    placeholder={t("pre-defined-jobs.job-title")}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    error={validationErrors?.title?.[0]}
                    disabled={!edit}
                    
                  />
                </div>

                <div className="col-span-full sm:col-span-1">
                  <Select
                    label={t("common.category")}
                    value={selectedCategory}
                    name="category"
                    error={validationErrors?.category_id?.[0]}
                    onChange={(e) => {
                      setSelectedCategory([parseInt(e.target.value)]);
                    }}
                    disabled={!edit}
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="col-span-full sm:col-span-1">
                  <Select
                    label={t("pre-defined-jobs.type-of-position")}
                    value={availability}
                    name="availability"
                    onChange={(e) => setAvailability(e.target.value)}
                    disabled={!edit}
                  >
                    <option value="full-time">Full Time</option>
                    <option value="part-time">Part Time</option>
                  </Select>
                </div>

                <div className="col-span-full sm:col-span-1">
                  <IconSelect
                    label="Job Icon"
                    value={selectedIcon}
                    onChange={setSelectedIcon}
                    error={validationErrors?.icon_name?.[0]}
                    placeholder="Select an icon..."
                    disabled={!edit}
                  />
                </div>
              </div>
            </div>

            {/* Job Details Section */}
            <div>
              <h3 className="text-base font-semibold leading-7 text-gray-900">
                {t("pre-defined-jobs.job-details")}
              </h3>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                Detailed information about the position.
              </p>

              <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                <div className="col-span-full sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-900">
                    {t("pre-defined-jobs.description")}
                  </label>
                  <div className="mt-2">
                    <ReactQuill
                      className="quill-light"
                      theme="snow"
                      value={description}
                      onChange={setDescription}
                      placeholder={t("pre-defined-jobs.description")}
                      readOnly={!edit}
                    />
                    {validationErrors?.description && (
                      <p className="mt-2 text-sm text-red-600">
                        {validationErrors.description[0]}
                      </p>
                    )}
                  </div>
                </div>

                <div className="col-span-full sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-900">
                    {t("pre-defined-jobs.required-experience")}
                  </label>
                  <div className="mt-2">
                    <ReactQuill
                      className="quill-light"
                      theme="snow"
                      value={experience}
                      onChange={setExperience}
                      placeholder={t("pre-defined-jobs.required-experience")}
                      readOnly={!edit}
                    />
                    {validationErrors?.experience && (
                      <p className="mt-2 text-sm text-red-600">
                        {validationErrors.experience[0]}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="border-t border-gray-900/10">
              <button
                type="button"
                onClick={clearFields}
                disabled={!edit}
                className="rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-900"
              >
                {t("common.clear")}
              </button>
              <button
                type="submit"
                disabled={loading || !edit}
                className="rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
              >
                {loading ? (
                  <i className="fad fa-circle-notch fa-spin" />
                ) : (
                  t("common.save")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PredefinedJobTemplate;
